import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import Pagination10 from '../Pagination10';
import { formatString } from '../../utils/StringUtils';
import { formatCurrency } from '../../utils/formatMoeda';
import { calculateTotal } from '../../utils/calculateUtil';
import ExcelUploadModal from '../modalServices/ExcelUploadModal';
import DefaultModal from './DefaultModal';
import useDefaultModal from '../hooks/useDefaultModal';
import ConfirmModal from './ConfirmModal';
import useConfirmModal from '../hooks/useConfirmModal';
import usePagination10 from '../hooks/usePagination10';
import LoadingModal from './LoadingModal';
import useProgressModal from '../hooks/useProgressModal';
import ProgressModal from './ProgressModal';
import ItemSearchModal from './ItemSearchModal';
import PesquisaPrecoMaterialListaModal from './PesquisaPrecoMaterialListaModal';
import PesquisaPrecoServicoListaModal from './PesquisaPrecoServicoListaModal';
import TableContainer from '../TableContainer';
import DataTable from '../DataTable';
import PaginationBar from '../PaginationBar';

function DemandaItensModal({ show, onClose, demanda }) {
  const [demandaItens, setDemandaItens] = useState([]);
  const [showExcelUploadModal, setShowExcelUploadModal] = useState(false);
  const [showItemSearchModal, setShowItemSearchModal] = useState(false);
  const [unidades, setUnidades] = useState([]);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [duplicateKeys, setDuplicateKeys] = useState(new Set());
  const [expandedDescs, setExpandedDescs] = useState(new Set());
  const [selectedItem, setSelectedItem] = useState(null);
  const [precosMap, setPrecosMap] = useState({});
  const [loadingPrecos, setLoadingPrecos] = useState(false);
  const [showPrecoListaModal, setShowPrecoListaModal] = useState(false);
  const [selectedItemForPreco, setSelectedItemForPreco] = useState(null);
  const [showServicoListaModal, setShowServicoListaModal] = useState(false);
  const [selectedItemForServico, setSelectedItemForServico] = useState(null);
  const token = localStorage.getItem('token');
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const {
    progressState,
    showProgressModal,
    updateProgress,
    hideProgressModal,
  } = useProgressModal();
  const cancelSave = useRef(false);

  const normalizeText = str => {
    return (str || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .toUpperCase()
      .trim();
  };

  const filteredDemandaItens = useMemo(() => {
    const normalizedSearch = normalizeText(searchTerm);
    return demandaItens.filter(
      item =>
        normalizeText(item.item?.toString()).includes(normalizedSearch) ||
        normalizeText(item.descricao).includes(normalizedSearch)
    );
  }, [searchTerm, demandaItens]);

  const totalItems = filteredDemandaItens.length;
  const { setPagination } = usePagination10(totalItems);
  const { startIndex, rowsPerPage } = setPagination;
  const selectedDemandaItens = filteredDemandaItens.slice(
    startIndex,
    startIndex + rowsPerPage
  );

  const calculateDuplicates = items => {
    const keyCounts = {};
    const duplicates = new Set();

    items.forEach(item => {
      const key = `${item.item}-${item.grupo}-${item.classe}-${
        item.pdm || 'N/A'
      }`;
      keyCounts[key] = (keyCounts[key] || 0) + 1;
    });

    items.forEach(item => {
      const key = `${item.item}-${item.grupo}-${item.classe}-${
        item.pdm || 'N/A'
      }`;
      if (keyCounts[key] > 1) duplicates.add(key);
    });

    return duplicates;
  };

  useEffect(() => {
    if (!show || !demanda) return;
    setLoading(true);
    axios
      .get(`${API_BASE_URL.DEMANDA_ITEM}/demanda`, {
        params: {
          cnpj: demanda.cnpj,
          num: demanda.num,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        const formattedData = result.data.map(item => ({
          ...item,
        }));
        setDemandaItens(formattedData);
        setDuplicateKeys(calculateDuplicates(formattedData));
      })
      .catch(error => {
        console.error('Erro ao obter listas:', error);
      });

    axios
      .get(`${API_BASE_URL.UNIDADE_MEDIDA}/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        setUnidades(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter unidades:', error);
      });

    axios
      .get(`${demanda?.tipo === 'SERVIÇO' ? API_BASE_URL.PESQUISA_PRECO_SERVICO : API_BASE_URL.PESQUISA_PRECO_MATERIAL}/resultados`, {
        params: {
          cnpj: demanda.cnpj,
          demandaNum: demanda.num,
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        const precos = result.data || [];
        const grouped = {};
        precos.forEach(p => {
          const codigo = p.codigoItemCatalogo;
          if (!grouped[codigo]) grouped[codigo] = [];
          grouped[codigo].push(p.precoUnitario);
        });
        const map = {};
        Object.entries(grouped).forEach(([codigo, values]) => {
          const numeros = values.filter(v => !isNaN(v));
          if (numeros.length === 0) return;
          const sorted = [...numeros].sort((a, b) => a - b);
          const trim = Math.max(1, Math.floor(sorted.length * 0.1));
          const trimmed = sorted.slice(trim, sorted.length - trim);
          const mediaSaneada = trimmed.length > 0
            ? trimmed.reduce((a, b) => a + b, 0) / trimmed.length
            : sorted.reduce((a, b) => a + b, 0) / sorted.length;
          const menor = Math.min(...numeros);
          const maior = Math.max(...numeros);
          const medio = numeros.reduce((a, b) => a + b, 0) / numeros.length;
          map[codigo] = { menor, medio, maior, mediaSaneada, quantidade: numeros.length };
        });
        setPrecosMap(map);
      })
      .catch(error => {
        console.error('Erro ao carregar preços:', error);
      });
    setLoading(false);
  }, [demanda, shouldFetch, token, show]);

  useEffect(() => {
    if (!show) {
      setSearchTerm('');
      setSelectedItem(null);
      setDemandaItens([]);
      setPrecosMap({});
    }
  }, [show]);

  const handleSearchChange = e => {
    setSearchTerm(e.target.value.toUpperCase());
  };

  const toggleDesc = (id) => {
    setExpandedDescs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const fetchPrecosFromDB = () => {
    if (!demanda) return;
    axios
      .get(`${demanda?.tipo === 'SERVIÇO' ? API_BASE_URL.PESQUISA_PRECO_SERVICO : API_BASE_URL.PESQUISA_PRECO_MATERIAL}/resultados`, {
        params: { cnpj: demanda.cnpj, demandaNum: demanda.num },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        const precos = result.data || [];
        const grouped = {};
        precos.forEach(p => {
          const codigo = p.codigoItemCatalogo;
          if (!grouped[codigo]) grouped[codigo] = [];
          grouped[codigo].push(p.precoUnitario);
        });
        const map = {};
        Object.entries(grouped).forEach(([codigo, values]) => {
          const numeros = values.filter(v => !isNaN(v));
          if (numeros.length === 0) return;
          const sorted = [...numeros].sort((a, b) => a - b);
          const trim = Math.max(1, Math.floor(sorted.length * 0.1));
          const trimmed = sorted.slice(trim, sorted.length - trim);
          const mediaSaneada = trimmed.length > 0
            ? trimmed.reduce((a, b) => a + b, 0) / trimmed.length
            : sorted.reduce((a, b) => a + b, 0) / sorted.length;
          const menor = Math.min(...numeros);
          const maior = Math.max(...numeros);
          const medio = numeros.reduce((a, b) => a + b, 0) / numeros.length;
          map[codigo] = { menor, medio, maior, mediaSaneada, quantidade: numeros.length };
        });
        setPrecosMap(map);
      })
      .catch(error => console.error('Erro ao recarregar preços:', error));
  };

  const executarBuscaPrecos = (onFeedback, onComplete) => {
    const notify = onFeedback || showDefaultModal;
    if (!demanda || !selectedItem || !selectedItem.item) return Promise.resolve();
    setLoadingPrecos(true);
    return axios
    .post(`${API_BASE_URL.PESQUISA_PRECO_MATERIAL}/pesquisar/item`, null, {
      params: {
        cnpj: demanda.cnpj,
        demandaNum: demanda.num,
        itemId: selectedItem.id,
        codigoItemCatalogo: selectedItem.item,
      },
      headers: { Authorization: `Bearer ${token}` },
    })
    .then(result => {
      const precos = result.data || [];
      const numeros = precos.map(p => p.precoUnitario).filter(v => !isNaN(v));
      const map = { ...precosMap };
      if (numeros.length > 0) {
        const sorted = [...numeros].sort((a, b) => a - b);
        const trim = Math.max(1, Math.floor(sorted.length * 0.1));
        const trimmed = sorted.slice(trim, sorted.length - trim);
        const mediaSaneada = trimmed.length > 0
          ? trimmed.reduce((a, b) => a + b, 0) / trimmed.length
          : sorted.reduce((a, b) => a + b, 0) / sorted.length;
        const menor = Math.min(...numeros);
        const maior = Math.max(...numeros);
        const medio = numeros.reduce((a, b) => a + b, 0) / numeros.length;
        map[selectedItem.item] = { menor, medio, maior, mediaSaneada, quantidade: numeros.length };
        notify('success', `${numeros.length} preço(s) encontrado(s) para o CATMAT ${selectedItem.item}.`);
      } else {
        delete map[selectedItem.item];
        notify('info', `Nenhum preço encontrado para o CATMAT ${selectedItem.item}.`);
      }
      setPrecosMap(map);
    })
    .catch(error => {
      console.error('Erro ao pesquisar preços:', error);
      notify('error', 'Erro ao buscar preços na ComprasGov.');
    })
    .finally(() => {
      setLoadingPrecos(false);
      if (onComplete) onComplete();
    });
  };

  const executarBuscaPrecosServico = (onFeedback, onComplete) => {
    const notify = onFeedback || showDefaultModal;
    if (!demanda || !selectedItem || !selectedItem.item) return Promise.resolve();
    setLoadingPrecos(true);
    return axios
    .post(`${API_BASE_URL.PESQUISA_PRECO_SERVICO}/pesquisar/item`, null, {
      params: {
        cnpj: demanda.cnpj,
        demandaNum: demanda.num,
        itemId: selectedItem.id,
        codigoItemCatalogo: selectedItem.item,
      },
      headers: { Authorization: `Bearer ${token}` },
    })
    .then(result => {
      const precos = result.data || [];
      const numeros = precos.map(p => p.precoUnitario).filter(v => !isNaN(v));
      const map = { ...precosMap };
      if (numeros.length > 0) {
        const sorted = [...numeros].sort((a, b) => a - b);
        const trim = Math.max(1, Math.floor(sorted.length * 0.1));
        const trimmed = sorted.slice(trim, sorted.length - trim);
        const mediaSaneada = trimmed.length > 0
          ? trimmed.reduce((a, b) => a + b, 0) / trimmed.length
          : sorted.reduce((a, b) => a + b, 0) / sorted.length;
        const menor = Math.min(...numeros);
        const maior = Math.max(...numeros);
        const medio = numeros.reduce((a, b) => a + b, 0) / numeros.length;
        map[selectedItem.item] = { menor, medio, maior, mediaSaneada, quantidade: numeros.length };
        notify('success', `${numeros.length} preço(s) encontrado(s) para o CATSER ${selectedItem.item}.`);
      } else {
        delete map[selectedItem.item];
        notify('info', `Nenhum preço encontrado para o CATSER ${selectedItem.item}.`);
      }
      setPrecosMap(map);
    })
    .catch(error => {
      console.error('Erro ao pesquisar preços de serviço:', error);
      notify('error', 'Erro ao buscar preços na ComprasGov (Serviço).');
    })
    .finally(() => {
      setLoadingPrecos(false);
      if (onComplete) onComplete();
    });
  };

  const fetchPrecos = () => {
    if (!demanda || !selectedItem || !selectedItem.item) return;
    showConfirmModal('atualizarPrecos', () => executarBuscaPrecos());
  };

  function handleDelete(demandaToDelete) {
    showConfirmModal('delete', () => handleDeleteConfirm(demandaToDelete));
  }

  function handleDeleteConfirm(itemToDelete) {
    axios
      .delete(`${API_BASE_URL.DEMANDA_ITEM}/delete`, {
        params: {
          cnpj: itemToDelete.cnpj,
          demandaNum: itemToDelete.demandaNum,
          id: itemToDelete.id,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        setShouldFetch(!shouldFetch);
        showDefaultModal('success', 'O registro foi excluído com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao excluir o item:', error);
        showDefaultModal('error', 'Erro ao excluir o registro!');
      });
  }

  const handleInputChange = (localIndex, field, value) => {
    const globalIndex = demandaItens.findIndex(
      item => item.id === selectedDemandaItens[localIndex].id
    );
    if (globalIndex === -1) return;

    const updated = [...demandaItens];
    updated[globalIndex][field] = value;

    setDemandaItens(updated);
    setDuplicateKeys(calculateDuplicates(updated));
  };

  const saveBatch = async batch => {
    return axios.post(`${API_BASE_URL.DEMANDA_ITEM}/saveAll`, batch, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      timeout: 30000,
    });
  };

  const handleSaveAll = async () => {
    cancelSave.current = false;
    const batchSize = 100;
    const totalItems = demandaItens.length;

    showProgressModal('Salvando Itens do DEMANDA', totalItems, () => {
      cancelSave.current = true;
      hideProgressModal();
    });

    await new Promise(resolve => setTimeout(resolve, 50));

    const results = {
      success: 0,
      failures: 0,
      errorDetails: [],
    };

    try {
      for (let i = 0; i < demandaItens.length; i += batchSize) {
        if (cancelSave.current) break;

        const batch = demandaItens.slice(i, i + batchSize);

        try {
          await saveBatch(batch);
          results.success += batch.length;
          const currentProgress = Math.min(i + batch.length, totalItems);
          updateProgress(currentProgress);
          await new Promise(resolve => setTimeout(resolve, 0));
        } catch (error) {
          results.failures += batch.length;
          results.errorDetails.push({
            batch: Math.floor(i / batchSize) + 1,
            error: error.response?.data?.message || error.message,
          });
          updateProgress(Math.min(i + batch.length, totalItems), 1);
        }
      }

      await new Promise(resolve => setTimeout(resolve, 300));
      hideProgressModal();

      if (!cancelSave.current && results.success > 0) {
        setShouldFetch(prev => !prev);
        showDefaultModal(
          'info',
          `Processo concluído! Sucessos: ${results.success}, Falhas: ${results.failures}`,
          results.errorDetails.length > 0 && {
            title: 'Erros encontrados',
            content: (
              <div className="small">
                <ul>
                  {results.errorDetails.map((err, idx) => (
                    <li key={idx}>
                      Lote {err.batch}: {err.error}
                    </li>
                  ))}
                </ul>
              </div>
            ),
          }
        );
      }
    } catch (error) {
      hideProgressModal();
      showDefaultModal('error', 'Erro durante o processamento');
    }
  };

  async function handleDownload() {
    try {
      const response = await axios.post(
        `${API_BASE_URL.DEMANDA}/exportExcel`,
        demanda,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `DEMANDA ${formatString(demanda.num, 3)}.xlsx`;
      link.click();
    } catch (error) {
      console.error('Erro ao baixar o arquivo:', error);
    }
  }

  const handleAddItemFromSearch = (newDemandaItem) => {
    axios
      .post(`${API_BASE_URL.DEMANDA_ITEM}/saveFind`, newDemandaItem, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(response => {
        showDefaultModal('success', response.data);
        setShouldFetch(!shouldFetch);
      })
      .catch(error => {
        if (error.response) {
          const errorMessage =
            error.response.data || 'Erro ao salvar o registro';
          console.error('Erro do servidor:', errorMessage);
          showDefaultModal('error', errorMessage);
        } else {
          console.error('Erro na requisição:', error.message);
          showDefaultModal('error', 'Erro de conexão');
        }
      });
  };

  if (!show) return null;

  return (
    <>
      <div
        className="modal show d-block"
        tabIndex="-1"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: '96vw', height: '90vh' }}
        >
          <div className="modal-content" style={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: '0.5rem', overflow: 'hidden' }}>
            <div
              className="modal-header text-white"
              style={{
                background:
                  'linear-gradient(45deg, rgb(19, 72, 156) 0%, rgba(19, 72, 156, 1) 100%)',
                padding: '0.35rem 0.75rem',
                border: 'none',
                flexShrink: 0,
              }}
            >
              <div className="d-flex align-items-center justify-content-between" style={{ width: '100%' }}>
                <h6 className="modal-title mb-0" style={{ fontSize: '0.80rem', lineHeight: 1 }}>
                  <i className="bi bi-list-ul me-2"></i>
                  Itens da Demanda Nº {demanda ? formatString(demanda.num, 4) : '--'}
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-link text-white p-0 mb-0"
                    onClick={onClose}
                    title="Fechar"
                    style={{ fontSize: '1rem', textDecoration: 'none' }}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-body" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '0.75rem' }}>
              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2" style={{ flexShrink: 0 }}>
                <div className="d-flex gap-2 flex-wrap">
                  <button
                    onClick={() => setShowItemSearchModal(true)}
                    className="btn btn-success btn-sm d-inline-flex align-items-center"
                    title="Adicionar novo item à Demanda"
                  >
                    <i className="bi bi-plus-circle me-1"></i>Adicionar Item
                  </button>
                  <button
                    onClick={handleSaveAll}
                    className="btn btn-primary btn-sm d-inline-flex align-items-center"
                    title="Salvar todos os itens"
                    disabled={progressState.show || isLoading}
                  >
                    {progressState.show ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Salvando...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-save me-1" />
                        Salvar Todos
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleDownload}
                    className="btn btn-info btn-sm d-inline-flex align-items-center"
                    title="Exportar Planilha Excel"
                    disabled={progressState.show}
                  >
                    <i className="bi bi-upload me-1" />
                    Exportar Excel
                  </button>
                  <button
                    className="btn btn-warning btn-sm d-inline-flex align-items-center"
                    title="Importar Planilha Excel"
                    onClick={() => setShowExcelUploadModal(true)}
                    disabled={progressState.show}
                  >
                    <i className="bi bi-download me-1" />
                    Importar Excel
                  </button>
                  <button
                    onClick={() => selectedItem && handleDelete(selectedItem)}
                    className="btn btn-danger btn-sm d-inline-flex align-items-center"
                    title="Excluir item selecionado"
                    disabled={!selectedItem || progressState.show}
                  >
                    <i className="bi bi-trash3 me-1" />
                    Excluir
                  </button>
                  {demanda?.tipo === 'MATERIAL' && (
                    <button
                      onClick={() => { setSelectedItemForPreco(selectedItem); setShowPrecoListaModal(true); }}
                      className="btn btn-outline-primary btn-sm d-inline-flex align-items-center"
                      title="Ver preços de material (CATMAT) do item selecionado"
                      disabled={!selectedItem}
                    >
                    <i className="bi bi-list-ul me-1" />
                    Lista de Preços
                    </button>
                  )}
                  {demanda?.tipo === 'SERVIÇO' && (
                    <button
                      onClick={() => { setSelectedItemForServico(selectedItem); setShowServicoListaModal(true); }}
                      className="btn btn-outline-success btn-sm d-inline-flex align-items-center"
                      title="Ver preços de serviço (CATSER) do item selecionado"
                      disabled={!selectedItem}
                    >
                    <i className="bi bi-list-ul me-1" />
                    Lista de Preços
                    </button>
                  )}
                </div>
                <div className="d-flex align-items-center">
                  <div
                    className="input-group input-group-sm"
                    style={{ width: '250px' }}
                  >
                    <span className="input-group-text">
                      <i className="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      id="searchFilter"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="form-control form-control-sm"
                      placeholder="Digite código ou descrição..."
                    />
                  </div>
                </div>
              </div>
              {duplicateKeys.size > 0 && (
                <div className="alert alert-warning small py-1 mb-2" style={{ flexShrink: 0 }}>
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Atenção: Existem itens duplicados (mesmo grupo, classe, pdm e
                  código). Verifique as linhas destacadas em vermelho.
                </div>
              )}
              <TableContainer isLoading={isLoading}>
                        <DataTable style={{ fontSize: '0.8rem' }} head={
                    <tr>
                          <th
                        scope="col"
                        className="text-center align-middle"
                        style={{ width: '5%' }}
                      >
                        Código
                      </th>
                      <th scope="col" className="text-center align-middle" style={{ width: '30%' }}>
                        Descrição
                      </th>
                      <th
                        scope="col"
                        className="text-center align-middle"
                        style={{ width: '10%' }}
                      >
                        Unidade
                      </th>
                      <th
                        scope="col"
                        className="text-center align-middle"
                        style={{ width: '10%' }}
                      >
                        Quantidade
                      </th>
                      <th
                        scope="col"
                        className="text-center align-middle"
                        style={{ width: '12%' }}
                      >
                        Vlr Unitário
                      </th>
                      <th
                        scope="col"
                        className="text-center align-middle"
                        style={{ width: '12%' }}
                      >
                        Vlr Total
                      </th>
                      <th
                        scope="col"
                        className="text-center align-middle"
                        style={{ width: '16%' }}
                      >
                        Preços ComprasGov
                      </th>
                    </tr>
                  }>
                  <tbody>
                    {!isLoading && selectedDemandaItens.length > 0 ? (
                      selectedDemandaItens.map((c, index) => {
                        const compositeKey = `${c.item}-${c.grupo}-${c.classe}-${
                          c.pdm || 'N/A'
                        }`;
                        const isDuplicate = duplicateKeys.has(compositeKey);
                        const isSelected = selectedItem && selectedItem.id === c.id;
                        return (
                          <tr
                            key={`${c.cnpj}-${c.demandaNum}-${c.id}`}
                            className={`${isDuplicate ? 'table-danger' : ''} ${isSelected ? 'table-primary' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelectedItem(isSelected ? null : c)}
                          >
                            <td style={{ width: '5%' }}>{c.item}</td>
                            <td>
                              <span>
                                {expandedDescs.has(c.id) || (c.descricao || '').length <= 100
                                  ? c.descricao
                                  : `${c.descricao.substring(0, 100)}...`}
                              </span>
                              {(c.descricao || '').length > 100 && (
                                <button
                                  className="btn btn-link btn-sm p-0 ms-1 text-decoration-none"
                                  style={{ fontSize: '0.7rem', verticalAlign: 'baseline' }}
                                  onClick={e => { e.stopPropagation(); toggleDesc(c.id); }}
                                >
                                  {expandedDescs.has(c.id)
                                    ? <><i className="bi bi-chevron-up"></i></>
                                    : <><i className="bi bi-chevron-down"></i></>
                                  }
                                </button>
                              )}
                            </td>
                            <td className="text-end">
                              <select
                                name="unidade"
                                value={c.unidade}
                                onChange={e =>
                                  handleInputChange(
                                    index,
                                    'unidade',
                                    e.target.value
                                  )
                                }
                                className="form-select form-select-sm"
                                style={{ minWidth: '100px' }}
                                disabled={progressState.show}
                              >
                                <option value="">Selecione...</option>
                                {unidades.map(unidade => (
                                  <option
                                    key={unidade.id}
                                    value={unidade.descricao}
                                  >
                                    {unidade.descricao}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="text-end">
                              <input
                                type="number"
                                name="quantidade"
                                value={c.quantidade ?? ''}
                                onChange={e =>
                                  handleInputChange(
                                    index,
                                    'quantidade',
                                    e.target.value
                                  )
                                }
                                className="form-control form-control-sm text-end"
                                style={{ minWidth: '100px' }}
                                min="0"
                                disabled={progressState.show}
                              />
                            </td>
                            <td className="text-end">
                              <div className="input-group input-group-sm">
                                <input
                                  type="number"
                                  name="vlrUnit"
                                  value={c.vlrUnit ?? ''}
                                  onChange={e =>
                                    handleInputChange(
                                      index,
                                      'vlrUnit',
                                      e.target.value
                                    )
                                  }
                                  className="form-control form-control-sm text-end"
                                  style={{ minWidth: '80px' }}
                                  min="0"
                                  step="0.01"
                                  disabled={progressState.show}
                                />
                                <button
                                  className="btn btn-outline-info btn-sm"
                                  type="button"
                                  title="Preencher com Média Saneada"
                                  disabled={progressState.show}
                                  onClick={e => {
                                    e.stopPropagation();
                                    const precos = precosMap[c.item];
                                    if (precos && precos.mediaSaneada != null) {
                                      handleInputChange(
                                        index,
                                        'vlrUnit',
                                        precos.mediaSaneada.toFixed(2)
                                      );
                                    }
                                  }}
                                >
                                  <i className="bi bi-calculator"></i>
                                </button>
                              </div>
                            </td>
                            <td className="text-end fw-semibold">
                              {formatCurrency(
                                (parseFloat(c.quantidade) || 0) * (parseFloat(c.vlrUnit) || 0)
                              )}
                            </td>
                            <td className="text-center">
                              {(() => {
                                const precos = precosMap[c.item];
                                if (!precos) {
                                  return <span className="text-muted small">Nenhum preço encontrado</span>;
                                }
                                return (
                                  <div className="d-flex justify-content-center gap-1">
                                    <div className="card border-success" style={{ width: '110px' }}>
                                      <div className="card-header bg-success text-white text-center p-0" style={{ fontSize: '0.6rem', fontWeight: 'bold' }}>
                                        Menor Preço
                                      </div>
                                      <div className="card-body text-center p-1">
                                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#198754' }}>
                                          {formatCurrency(precos.menor)}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="card border-primary" style={{ width: '110px' }}>
                                      <div className="card-header bg-primary text-white text-center p-0" style={{ fontSize: '0.6rem', fontWeight: 'bold' }}>
                                        Preço Médio
                                      </div>
                                      <div className="card-body text-center p-1">
                                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#0d6efd' }}>
                                          {formatCurrency(precos.medio)}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="card border-danger" style={{ width: '110px' }}>
                                      <div className="card-header bg-danger text-white text-center p-0" style={{ fontSize: '0.6rem', fontWeight: 'bold' }}>
                                        Maior Preço
                                      </div>
                                      <div className="card-body text-center p-1">
                                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#dc3545' }}>
                                          {formatCurrency(precos.maior)}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="card border-info" style={{ width: '110px' }}>
                                      <div className="card-header bg-info text-white text-center p-0" style={{ fontSize: '0.6rem', fontWeight: 'bold' }}>
                                        Média Saneada
                                      </div>
                                      <div className="card-body text-center p-1">
                                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#0dcaf0' }}>
                                          {formatCurrency(precos.mediaSaneada)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center">
                          Nenhum registro encontrado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </DataTable>
              </TableContainer>
            </div>
            <PaginationBar setPagination={setPagination}/>
          </div>
        </div>
      </div>
      <ExcelUploadModal
        show={showExcelUploadModal}
        onClose={() => {
          setShowExcelUploadModal(false);
          setShouldFetch(prev => !prev);
        }}
        onSuccess={() => setShouldFetch(prev => !prev)}
        dfd={demanda}
      />
      <ItemSearchModal
        show={showItemSearchModal}
        onClose={() => {
          setShowItemSearchModal(false);
          setShouldFetch(prev => !prev);
        }}
        onAdd={handleAddItemFromSearch}
        demanda={demanda}
      />
      <ConfirmModal {...setConfirmModal} />
      <DefaultModal {...setDefaultModal} />
      <PesquisaPrecoMaterialListaModal
        show={showPrecoListaModal}
        onClose={() => setShowPrecoListaModal(false)}
        demanda={demanda}
        item={selectedItemForPreco}
        onDelete={fetchPrecosFromDB}
        buscarPrecos={executarBuscaPrecos}
      />
      <PesquisaPrecoServicoListaModal
        show={showServicoListaModal}
        onClose={() => setShowServicoListaModal(false)}
        demanda={demanda}
        item={selectedItemForServico}
        onDelete={fetchPrecosFromDB}
        buscarPrecos={executarBuscaPrecosServico}
      />
      <ProgressModal
        show={progressState.show}
        title={progressState.title}
        progress={progressState.progress}
        current={progressState.current}
        total={progressState.total}
        errors={progressState.errors}
        onCancel={progressState.onCancel}
      />
      <LoadingModal loading={loadingPrecos} message="Buscando preços na ComprasGov..." />
    </>
  );
}

export default DemandaItensModal;
